import 'leaflet/dist/leaflet.css';

import { DownloadCsvButton } from '@/components/buttons';
import { AdminContainer } from '@/components/containers';
import { Loading } from '@/components/loading';
import { LoadingOverlay } from '@/components/LoadingOverlay/LoadingOverlay';
import { ProjectDetailsTabs, ProjectToggle, ToggleTabs } from '@/components/toggle/toggle.component';
import { cn } from '@/lib/utils';
import MarkProjectAsComplete from '@/modules/MarkAsComplete/MarkProjectAsComplete';
import { IfPermitted, UserPermissions } from '@/modules/Permissions';
import { ProjectDetailList } from '@/modules/ProjectDetails/ProjectDetailList';
import { ProjectDetailsMap } from '@/modules/ProjectDetails/ProjectDetailsMap';
import { projectStatus } from '@/modules/Projects/Projects.constants';
import { useProjectData, useProjectFarmSummaryData, useProjectReportStatus } from '@/modules/Report/Report.api';
import { reportStatus as reportStatusValues } from '@/modules/Report/Report.schema';
import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { ProjectReport } from './ProjectReport';

function ProjectDetails() {
  const { customerId, projectId } = useParams<{ customerId: string; projectId: string }>();

  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') as ToggleTabs) || ProjectDetailsTabs.LIST;
  const [visibleTab, setVisibleTab] = useState<ToggleTabs>(initialTab);

  const [reportRefetching, setReportRefetching] = useState(false);
  const {
    data: reportData,
    status: reportQueryStatus,
    refetch: reportRefetch,
  } = useProjectReportStatus(projectId, reportRefetching ? 2000 : false);

  const { data: projectData, status: projectQueryStatus, error: projectQueryError } = useProjectData(projectId);

  const { data: farmSummaries, status: farmSummaryStatus } = useProjectFarmSummaryData(projectId || '');

  const isComplete = projectData?.status === projectStatus.COMPLETE;
  const reportingEnabled = projectData?.reporting_enabled === true;
  const isCsvDownloadable = projectData?.csv_downloadable === true;

  useEffect(() => {
    const current = searchParams.get('tab');
    if (current !== visibleTab) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('tab', visibleTab);
      setSearchParams(newParams, { replace: true });
    }
  }, [visibleTab, searchParams, setSearchParams]);

  // Starts report refetching. Looks at the report tab and the report status to start polling
  useEffect(() => {
    const shouldRefetch = visibleTab === ProjectDetailsTabs.REPORT && reportData?.status !== reportStatusValues.COMPLETED;

    if (reportRefetching !== shouldRefetch) {
      setReportRefetching(shouldRefetch);
    }
  }, [visibleTab, reportData?.status, reportRefetching]);

  // Stops report refetching. Looks at the report query status and the report status to stop polling
  useEffect(() => {
    if (reportQueryStatus === 'success' && reportData?.status === reportStatusValues.COMPLETED && reportRefetching) {
      setReportRefetching(false);
    }
  }, [reportData, reportQueryStatus, reportRefetching]);

  const callToAction = useMemo(() => {
    return (
      <div className="flex items-center justify-end gap-4">
        {!isComplete && <MarkProjectAsComplete projectId={projectId} isComplete={isComplete} setReportRefetching={setReportRefetching} />}
        {isCsvDownloadable && <DownloadCsvButton projectId={projectId} template="project_csv_samples" />}
      </div>
    );
  }, [isComplete, projectId, isCsvDownloadable]);

  const hasFarmData = farmSummaryStatus === 'success' && Array.isArray(farmSummaries) && farmSummaries.length > 0;
  const showMap = hasFarmData;
  const toggle = <ProjectToggle visibleTab={visibleTab} setVisibleTab={setVisibleTab} showMap={showMap} />;

  if (projectQueryStatus === 'error') {
    throw projectQueryError;
  }

  if (!projectId || !customerId) {
    return;
  }

  return (
    <LoadingOverlay isLoading={projectQueryStatus === 'pending'} message="Checking Permissions...">
      <AdminContainer
        className={cn('flex flex-col')}
        title={projectData?.project_name || 'Loading project...'}
        breadcrumbData={projectData?.breadcrumb}
        callToAction={callToAction}
        searchAndFilter={toggle}
      >
        {projectQueryStatus === 'pending' ? (
          <Loading />
        ) : (
          <IfPermitted
            permissions={[]}
            conditions={[
              {
                permissions: [UserPermissions.PROJECT_DETAIL],
                condition: () => reportingEnabled,
              },
              {
                permissions: [UserPermissions.PROJECT_DETAIL_COMPLETE],
                condition: () => reportingEnabled && isComplete,
              },
            ]}
          >
            {visibleTab === ProjectDetailsTabs.LIST && <ProjectDetailList projectData={projectData} />}
            {visibleTab === ProjectDetailsTabs.MAP && showMap && (
              <ProjectDetailsMap customerId={customerId} projectId={projectId} reportingType={projectData?.reporting_type} />
            )}
            {visibleTab === ProjectDetailsTabs.REPORT && (
              <ProjectReport
                visibleTab={visibleTab === ProjectDetailsTabs.REPORT}
                projectId={projectId}
                data={reportData}
                status={reportQueryStatus}
                refetch={reportRefetch}
                setRefetching={setReportRefetching}
              />
            )}
          </IfPermitted>
        )}
      </AdminContainer>
    </LoadingOverlay>
  );
}

export default ProjectDetails;
